import React, { useEffect, useState } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';

type DashboardOverview = {
  counters: {
    joueurs_en_ligne: number;
    interventions_actives: number;
    signalements_attente: number;
    staff_en_ligne: number;
  };
  interventions: Array<{
    id: number;
    scenario_id: number;
    numero_intervention: string;
    adresse: string;
    statut: string;
    created_at: string;
    scenario_nom: string | null;
    service_nom: string | null;
  }>;
  reports: Array<{
    id: number;
    title: string;
    category: string;
    status: string;
    created_at: string;
    username: string | null;
  }>;
  alerts: Array<{
    id: number;
    message: string;
    severity: string;
    created_at: string;
  }>;
  cta_summary: Array<{
    status: string;
    total: number;
  }>;
  realtime?: {
    panel_interventions: Array<{ id: number; created_at: string }>;
    signalements: Array<{ id: number; created_at: string }>;
  };
};

const PanelDashboardV1: React.FC = () => {
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const run = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          if (isMounted) setError('Token manquant');
          return;
        }
        const overviewResponse = await fetch(`${getApiBaseUrl(true)}/panel/v1/dashboard/overview`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        if (overviewResponse.status !== 404) {
          const overviewData = await overviewResponse.json();
          if (isMounted && overviewResponse.ok && overviewData?.success) {
            setOverview(overviewData.data as DashboardOverview);
          }
        }
      } catch (e: any) {
        if (isMounted) setError(e?.message || 'Erreur de chargement');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    void run();
    const timer = window.setInterval(() => {
      void run();
    }, 15000);
    return () => {
      isMounted = false;
      window.clearInterval(timer);
    };
  }, []);

  if (loading) return <div className="text-cockpit-text-2">Chargement dashboard V1...</div>;
  if (error) return <div className="text-cockpit-red">{error}</div>;

  const cards = [
    { title: 'Joueurs en ligne', value: String(overview?.counters.joueurs_en_ligne ?? 0), trend: 'Sessions actives', valueColor: 'text-cockpit-violet' },
    { title: 'Interventions actives', value: String(overview?.counters.interventions_actives ?? 0), trend: 'Statuts DISPONIBLE / EN_COURS', valueColor: 'text-cockpit-blue' },
    { title: 'Signalements en attente', value: String(overview?.counters.signalements_attente ?? 0), trend: 'Sévérité medium à critical (24h)', valueColor: 'text-cockpit-amber' },
    { title: 'Staff en ligne', value: String(overview?.counters.staff_en_ligne ?? 0), trend: 'Rôles ≤ 16 (équipe)', valueColor: 'text-cockpit-green' },
  ];

  const cleanAdresse = (raw: string | null | undefined): string => {
    if (!raw) return 'Non renseigné';
    // Supprimer les coordonnées GPS de type "(43.6176, 3.8533) — " en début de chaîne
    return raw.replace(/^\([-\d.,\s]+\)\s*[—–-]+\s*/u, '').trim() || raw;
  };

  const interventions = (overview?.interventions || []).map((it) => ({
    id: it.numero_intervention || `INT-${it.id}`,
    type: it.scenario_nom || `Scénario #${it.scenario_id || it.id}`,
    lieu: cleanAdresse(it.adresse),
    service: it.service_nom || 'N/A',
    status: it.statut === 'EN_COURS' ? 'En cours' : it.statut === 'DISPONIBLE' ? 'En attente' : it.statut === 'TERMINEE' ? 'Terminee' : 'Annulee',
  }));

  const alerts = (overview?.alerts || []).map((a) => {
    const severity = String(a.severity || '').toLowerCase();
    if (severity === 'critical') {
      return {
        id: a.id,
        message: a.message,
        tone: 'border-cockpit-red/40 bg-cockpit-red/10 text-cockpit-red',
        emoji: '🚨',
        label: 'Critique',
      };
    }
    if (severity === 'high') {
      return {
        id: a.id,
        message: a.message,
        tone: 'border-cockpit-amber/40 bg-cockpit-amber/10 text-cockpit-amber',
        emoji: '⚠️',
        label: 'Élevée',
      };
    }
    return {
      id: a.id,
      message: a.message,
      tone: 'border-cockpit-blue/40 bg-cockpit-blue/10 text-cockpit-blue',
      emoji: 'ℹ️',
      label: 'Info',
    };
  });

  const reports = (overview?.reports || []).map((r) => ({
    id: `#SUP-${r.id}`,
    user: r.username || 'Joueur',
    reason: r.title || r.category || 'Ticket support',
    ago: new Date(r.created_at).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
    priority: r.status === 'open' ? 'Ouvert' : r.status === 'closed' ? 'Fermé' : r.status,
  }));

  const nowDate = new Date();
  const now = nowDate.getTime();
  const dayStart = new Date(nowDate);
  dayStart.setHours(0, 0, 0, 0);
  const dayStartMs = dayStart.getTime();
  const bucketCount = 24; // 24 heures (00h -> 23h)
  const currentPlayers = Number(overview?.counters.joueurs_en_ligne || 0);
  const currentHour = nowDate.getHours();
  const playersLine = new Array<number>(bucketCount).fill(0);
  playersLine[currentHour] = currentPlayers;
  const interventionsBuckets = new Array<number>(bucketCount).fill(0);
  const reportsBuckets = new Array<number>(bucketCount).fill(0);
  const pushEventToBucket = (target: number[], isoDate?: string | null) => {
    if (!isoDate) return;
    const ts = new Date(isoDate).getTime();
    if (Number.isNaN(ts)) return;
    if (ts < dayStartMs || ts > now) return;
    const hourIndex = new Date(ts).getHours();
    target[hourIndex] += 1;
  };
  (overview?.realtime?.panel_interventions || []).forEach((i) => pushEventToBucket(interventionsBuckets, i.created_at));
  (overview?.realtime?.signalements || []).forEach((r) => pushEventToBucket(reportsBuckets, r.created_at));
  const maxSeriesValue = Math.max(1, ...playersLine, ...interventionsBuckets, ...reportsBuckets);
  const roundedMax = maxSeriesValue <= 3 ? 3 : Math.ceil(maxSeriesValue / 5) * 5;
  const yTicks = [roundedMax, Math.round(roundedMax * 0.75), Math.round(roundedMax * 0.5), Math.round(roundedMax * 0.25), 0];
  const xLabels = new Array<string>(bucketCount).fill('').map((_, i) => `${String(i).padStart(2, '0')}h`);
  const chartW = 940;
  const chartH = 175;
  // Utilise la même échelle que les graduations Y pour éviter les décalages visuels.
  const toY = (v: number) => chartH - (v / roundedMax) * (chartH - 10) - 5;
  const toX = (index: number) => (index / (bucketCount - 1)) * (chartW - 20) + 10;
  const stepPath = (series: number[]) => {
    if (!series.length) return '';
    let path = `M ${toX(0).toFixed(1)} ${toY(series[0]).toFixed(1)}`;
    for (let i = 0; i < series.length - 1; i += 1) {
      const xNext = toX(i + 1).toFixed(1);
      const yCurrent = toY(series[i]).toFixed(1);
      const yNext = toY(series[i + 1]).toFixed(1);
      path += ` L ${xNext} ${yCurrent} L ${xNext} ${yNext}`;
    }
    return path;
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <div
            key={card.title}
            className="rounded-md border border-cockpit-line/70 bg-cockpit-panel/70 p-3 shadow-[0_12px_24px_rgba(2,6,23,0.35)] backdrop-blur-sm"
          >
            <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-cockpit-muted">{card.title}</div>
            <div className={`mt-2 font-mono text-2xl font-bold ${card.valueColor}`}>{card.value}</div>
            <div className="mt-1 text-xs text-cockpit-text-2">{card.trend}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.2fr_0.8fr]">
        <div className="space-y-4">
          <div className="rounded-md border border-cockpit-line/70 bg-cockpit-panel/70 p-4 backdrop-blur-sm">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-cockpit-text">Activite en temps reel</h3>
              <span className="rounded bg-cockpit-panel-2 px-2 py-1 text-[11px] text-cockpit-text-2">Aujourd'hui (00h-23h)</span>
            </div>
            <div className="h-56 rounded-md border border-cockpit-line bg-cockpit-bg-2/60 p-2.5">
              <svg viewBox={`0 0 ${chartW} ${chartH + 18}`} className="h-full w-full">
                {[0, 1, 2, 3, 4].map((r) => {
                  const y = (r / 4) * (chartH - 10) + 5;
                  return <line key={r} x1="10" y1={y} x2={chartW - 10} y2={y} stroke="rgba(148,163,184,0.18)" strokeWidth="1" />;
                })}
                {Array.from({ length: bucketCount }).map((_, i) => (
                  <line key={i} x1={toX(i)} y1="5" x2={toX(i)} y2={chartH - 5} stroke="rgba(148,163,184,0.08)" strokeWidth="1" />
                ))}
                {yTicks.map((v, i) => {
                  const y = (i / 4) * (chartH - 10) + 5;
                  return (
                    <text key={`yt-${v}-${i}`} x="2" y={y + 3} fill="#9fb0c6" fontSize="10">
                      {v}
                    </text>
                  );
                })}
                <path d={stepPath(playersLine)} fill="none" stroke="#a78bfa" strokeWidth="2.4" />
                <path d={stepPath(interventionsBuckets)} fill="none" stroke="#10b981" strokeWidth="2.4" />
                <path d={stepPath(reportsBuckets)} fill="none" stroke="#f59e0b" strokeWidth="2.4" />
                {xLabels.map((label, i) => (
                  <text key={`xt-${label}-${i}`} x={toX(i) - 8} y={chartH + 12} fill="#9fb0c6" fontSize="9">
                    {label}
                  </text>
                ))}
              </svg>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-cockpit-text-2">
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-cockpit-violet" /> Joueurs</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-cockpit-green" /> Interventions</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-cockpit-amber" /> Signalements</span>
            </div>
          </div>

          <div className="rounded-md border border-cockpit-line/70 bg-cockpit-panel/70 p-4 backdrop-blur-sm">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-cockpit-text">Alertes importantes</h3>
            <ul className="space-y-2 text-sm text-cockpit-text-2">
              {alerts.length > 0 ? (
                alerts.map((item) => (
                  <li key={item.id} className={`rounded border px-3 py-2 ${item.tone}`}>
                    <span className="mr-2">{item.emoji}</span>
                    <span className="mr-2 font-semibold">[{item.label}]</span>
                    <span>{item.message}</span>
                  </li>
                ))
              ) : (
                <li className="rounded border border-cockpit-line-soft bg-cockpit-bg-2/60 px-3 py-2 text-cockpit-text-2">
                  ✅ Aucune alerte critique récente
                </li>
              )}
            </ul>
          </div>

        </div>

        <div className="space-y-4">
          <div className="rounded-md border border-cockpit-line/70 bg-cockpit-panel/70 p-4 backdrop-blur-sm">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-cockpit-text">Signalements récents</h3>
            <div className="space-y-1.5 text-xs">
              {(reports.length > 0 ? reports : []).map((report) => (
                <div key={report.id} className="grid grid-cols-[0.85fr_1fr_1.1fr_auto_auto] items-center gap-2 rounded border border-cockpit-line-soft bg-cockpit-bg-2/60 px-2 py-1.5">
                  <span className="font-mono text-cockpit-text-2">{report.id}</span>
                  <span className="text-cockpit-text">{report.user}</span>
                  <span className="text-cockpit-muted">{report.reason}</span>
                  <span className="font-mono text-cockpit-muted">{report.ago}</span>
                  <span
                    className={`rounded px-2 py-0.5 ${
                      report.priority === 'Ouvert'
                        ? 'bg-cockpit-red/20 text-cockpit-red'
                        : report.priority === 'in_progress'
                          ? 'bg-cockpit-amber/20 text-cockpit-amber'
                          : report.priority === 'pending'
                            ? 'bg-cockpit-amber/20 text-cockpit-amber'
                            : 'bg-cockpit-green/20 text-cockpit-green'
                    }`}
                  >
                    {report.priority}
                  </span>
                </div>
              ))}
              {reports.length < 1 && (
                <div className="rounded border border-cockpit-line-soft bg-cockpit-bg-2/60 px-3 py-2 text-cockpit-muted">
                  Aucun signalement récent en base.
                </div>
              )}
            </div>
          </div>

          <div className="rounded-md border border-cockpit-line/70 bg-cockpit-panel/70 p-4 backdrop-blur-sm">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-cockpit-text">Interventions en cours</h3>
            <div className="space-y-2">
              {(interventions.length > 0 ? interventions : [{ id: 'AUCUNE', type: 'Aucune intervention', lieu: '-', service: '-', status: 'Terminee' }]).map((it) => (
                <div key={it.id} className="grid grid-cols-[1.1fr_1fr_auto] items-center gap-2 rounded border border-cockpit-line-soft bg-cockpit-bg-2/60 px-3 py-2 text-xs backdrop-blur-sm">
                  <div>
                    <div className="font-mono font-medium text-cockpit-text">{it.id}</div>
                    <div className="text-cockpit-text-2">{it.type}</div>
                    <div className="text-cockpit-muted">Service: {it.service}</div>
                  </div>
                  <div className="text-cockpit-text-2">{it.lieu}</div>
                  <span
                    className={`rounded px-2 py-1 ${
                      it.status === 'En cours'
                        ? 'bg-cockpit-amber/20 text-cockpit-amber'
                        : it.status === 'Terminee'
                          ? 'bg-cockpit-green/20 text-cockpit-green'
                          : 'bg-cockpit-blue/20 text-cockpit-blue'
                    }`}
                  >
                    {it.status}
                  </span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

    </div>
  );
};

export default PanelDashboardV1;
